<template>
  <v-menu v-model="showDialog" :nudge-bottom="32" left>
    <template v-slot:activator="{ on, attrs }">
      <v-btn v-on="on" v-bind="attrs" rbt-target="spaceDetails" large plain>
        <v-icon left>mdi-information-outline</v-icon>

        <span>
          {{ t("buttonActivator") }}
        </span>
      </v-btn>
    </template>

    <ul class="space-details">
      <li v-if="spaceMetadata.region" class="space-details__item">
        <span class="space-details__item__label">
          {{ t("region") }}
        </span>

        <div class="space-details__item__content">
          <span>{{ spaceMetadata.region.name }}</span>
          <v-icon :size="12">mdi-chevron-double-right</v-icon>
          <span>{{ spaceMetadata.region.code }}</span>
        </div>
      </li>

      <li v-if="spaceMetadata.regionZone" class="space-details__item">
        <span class="space-details__item__label">
          {{ t("availabilityZone") }}
        </span>

        <div class="space-details__item__content">
          <span>{{ spaceMetadata.regionZone }}</span>
        </div>
      </li>

      <li v-if="vpcId" :class="['space-details__item', 'space-details__item--divider-top']">
        <span class="space-details__item__label">
          {{ t("vpcId") }}
        </span>

        <div class="space-details__item__content">
          <span>{{ vpcId }}</span>

          <v-btn @click="copyVpcId" text icon>
            <v-icon>action_copy</v-icon>
          </v-btn>

          <app-tooltip :text="t('openAwsConsole')">
            <v-btn @click="openAwsConsole" text icon>
              <img :src="VizNowAwsIcon" alt="aws logo" />
            </v-btn>
          </app-tooltip>
        </div>
      </li>

      <li
        :class="[
          'space-details__item',
          (spaceMetadata.regionZone || spaceMetadata.region) && 'space-details__item--divider-top'
        ]"
      >
        <span class="space-details__item__label">
          {{ t("owner") }}
        </span>

        <div class="space-details__item__content">
          <span>{{ spaceMetadata.owner }}</span>
        </div>
      </li>

      <li v-if="spaceMetadata.template" :class="['space-details__item']">
        <span class="space-details__item__label">
          {{ t("template") }}
        </span>

        <div class="space-details__item__content">
          <span>{{ spaceMetadata.template }}</span>
        </div>
      </li>
    </ul>
  </v-menu>
</template>

<script lang="ts">
import { type PropType } from "@vue/composition-api"
import { defineComponent, computed, ref } from "@vue/composition-api"
import { WorkspaceTemplate } from "@/types/workspace"
import { getRegion, getTemplate } from "../../lib/space-details"
import { createTranslationModule } from "@/plugins/i18n"
import { copyToClipboard } from "@/utils/ui"
import { raiseError, raiseSuccess } from "@/utils/event-bus"
import { getAwsConsoleUrl } from "@/config/aws-console"
import AppTooltip from "@/components/tooltip/TooltipRoot.vue"
import VizNowAwsIcon from "@/assets/viznow-aws-icon.svg"

export default defineComponent({
  name: "SpaceDetails",
  props: {
    space: {
      type: Object as PropType<WorkspaceTemplate>,
      required: true
    }
  },
  setup(props) {
    const t = createTranslationModule("WorkspaceEdit.metadata.SpaceDetail")
    const showDialog = ref(false)

    const spaceMetadata = computed(() => {
      return {
        region: getRegion(props.space),
        regionZone: props.space.cloudZoneAZ,
        owner: props.space.username,
        template: getTemplate(props.space)
      }
    })

    const vpcId = computed(() => {
      return props.space.vpcId || null
    })

    const copyVpcId = async () => {
      if (vpcId.value) {
        try {
          await copyToClipboard(vpcId.value)
          raiseSuccess({ text: t("prompt.onCopy") })
        } catch {
          raiseError({ text: t("prompt.onError") })
        }
      }
    }

    const openAwsConsole = () => {
      const spaceRegion = props.space?.cloudZone

      if (vpcId.value && spaceRegion) {
        const consoleUrl = getAwsConsoleUrl(spaceRegion, vpcId.value)
        window.open(consoleUrl, "_blank")
      } else {
        raiseError({ text: t("prompt.onOpenAwsConsoleError") })
      }
    }

    return {
      VizNowAwsIcon,
      t,
      openAwsConsole,
      copyVpcId,
      vpcId,
      spaceMetadata,
      showDialog
    }
  },
  components: {
    AppTooltip
  }
})
</script>
